import { Moment } from "moment";
import * as Sentry from "@sentry/browser";
import { htmlEncode, jQueryAjaxConverters } from "./helpers";
import { stringFormat } from "./helpers";
import { translate } from "./translate";
import * as TranslationsKeys from "./locale/translationsKeys";
import { dialogConfirmationButtonClassName, dialogCancelButtonClassName } from "./dialogConstants";
import { EventData, trackEvent } from "../Features/Analytics";
import { httpStatusCodeDescription } from "./httpStatusCodes";

const initialCompletionTimeout = 500;
const subsequentCompletionTimeout = 1000;
const maxWaitForJobCompletionAttempts = 900; // 15 minutes wait for the job completion.
const maxJobHandlingAttempts = 5; // keep the value in sync with appsettings.json Rebus:MaxDeliveryAttempts
export const REQUEST_ID_HEADER = 'X-Request-Id';

export function formSubmitWithWaitForJobCompletion(
    form: JQuery<HTMLElement>,
    submitButton: JQuery<HTMLElement>,
    jobCompletionUrl: string,
    onCompletion: (job: JobDetail) => void,
    confirmationMessage: string | undefined = undefined,
    shouldEnableDataValidationFormParameterName: string | undefined = undefined,
    shouldScrollToSummerNoteEditorOnValidationError: boolean = true,
    onFailure: () => void = (() => { })
)
{
    if (!form.length) return;

    attachValidationAfterTurboLoad();

    // taken from https://stackoverflow.com/a/18485054/37927 and https://stackoverflow.com/a/10899796/379279
    form.submit(function (e) {
        e.preventDefault();

        if (isElementDisabled(submitButton)) {
            // second form submit was kicked off after a remote validation completion
            return false;
        }
        
        const formData = new FormData(e.currentTarget as HTMLFormElement);
        const shouldEnableDataValidation = shouldEnableDataValidationFormParameterName ?
            formData.get(shouldEnableDataValidationFormParameterName) !== "false" :
            true;

        if (shouldEnableDataValidation && !isFormValid(form)) {
            if (shouldScrollToSummerNoteEditorOnValidationError) {
                makeSureToScrollToSummerNoteEditorOnValidationError();
            }
            return false;
        }

        const actionUrlVal = $(e.currentTarget).attr('action');
        if (!actionUrlVal) {
            throw new Error('actionUrl not found');
        }
        const actionUrl = actionUrlVal;

        if (confirmationMessage) {
            bootbox.confirm({
                message: confirmationMessage,
                buttons: {
                    confirm: {
                        label: translate(TranslationsKeys.Global_Ok),
                        className: dialogConfirmationButtonClassName
                    },
                    cancel: {
                        label: translate(TranslationsKeys.Global_Cancel),
                        className: dialogCancelButtonClassName
                    }
                },
                callback(result) {
                    if (!result) return;

                    postRequestAndWaitForJobCompletionCommon();
                }
            });
        } else {
            postRequestAndWaitForJobCompletionCommon();
        }

        return false;

        function postRequestAndWaitForJobCompletionCommon() {
            postRequestAndWaitForJobCompletion(actionUrl, formData, jobCompletionUrl, submitButton, onCompletion, false, "POST", onFailure);
        }

        function makeSureToScrollToSummerNoteEditorOnValidationError() {
            const inputsWithValidationError = $(".input-validation-error");
            if (inputsWithValidationError.length > 0) {
                const firstInputWithValidationError = inputsWithValidationError.first();
                if (!firstInputWithValidationError.is(":visible")) {
                    firstInputWithValidationError.next()[0].scrollIntoView();
                }
            }
        }
    });
}

function isFormValid(form: JQuery<Element>) {
    return form.valid() && isRemoteValidationCompleted(form);
}

// taken from https://stackoverflow.com/a/59359068/379279
function isRemoteValidationCompleted(form) {
    return form.validate().pendingRequest === 0;
}

function attachValidationAfterTurboLoad() {
    $.validator.unobtrusive.parse(document); // on Turbo load (not the first page load), validation is not attached
}

export function ajaxErrorHandler(
    jqXhr: JQuery.jqXHR,
    textStatus: JQuery.Ajax.ErrorTextStatus,
    errorThrown: string,
    actionUrl: string,
    formData: Record<string, any> | undefined,
    errorTitle = "Error"
) {
    const errorLines: string[] = [];

    const isValidationError = jqXhr.responseJSON &&
        jqXhr.responseJSON.title &&
        jqXhr.responseJSON.errors &&
        typeof jqXhr.responseJSON.errors === 'object';
    if (isValidationError) {
        const errors = jqXhr.responseJSON.errors as Record<string, string>;
        errorLines.push(htmlEncode(jqXhr.responseJSON.title) + '<br>');
        for (const prop in errors) {
            errorLines.push(htmlEncode(errors[prop]));
        }
    }
    else {
        errorLines.push(`HTTP status code: ${jqXhr.status} ${httpStatusCodeDescription[jqXhr.status]} ${htmlEncode(errorThrown)}`);
    }

    const requestId = jqXhr.getResponseHeader('X-Request-Id');

    showErrorDialog(errorLines.join('<br>'), `${requestId}`);
    console.error("Request error", errorTitle, actionUrl, formData, jqXhr.status, errorThrown, jqXhr.responseJSON || jqXhr.responseText, jqXhr.getAllResponseHeaders());
}

export function postRequestAndWaitForJobCompletion(
    actionUrl: string,
    formData: Record<string, any> | FormData | undefined,
    jobCompletionUrl: string,
    submitButton: JQuery<HTMLElement> | undefined = undefined,
    onCompletion: (job: JobDetail) => void = (() => {}),
    ajaxStyle = false,
    method = 'POST',
    onFailure: () => void = (() => { })
)
{
    disableElementAndAddSpinner(submitButton);

    $.ajax({
        url: actionUrl,
        method,
        data: formData,
        success: (jobGuid) => {
            scheduleWaitForJobCompletion(jobCompletionUrl, jobGuid, onCompletion, initialCompletionTimeout, submitButton, 1);
        },
        error: (jqXhr, textStatus, errorThrown) => {
            onFailure();
            ajaxErrorHandler(jqXhr, textStatus, errorThrown, actionUrl, formData);

            enableElementAndRemoveSpinner(submitButton);
        },
        contentType: ajaxStyle ? undefined : false,
        processData: ajaxStyle ? undefined : false
    });
}

export function scheduleWaitForJobCompletion(
    jobCompletionUrl: string,
    jobGuid: string,
    onCompletion: (job: JobDetail) => void = () => { },
    timeout: number = initialCompletionTimeout,
    submitButton: JQuery<HTMLElement> | undefined = undefined,
    attemptCount = 1
)
{
    setTimeout(() => {
        waitForJobCompletion(jobCompletionUrl, jobGuid, submitButton, onCompletion, attemptCount);
        },
        timeout
    );
}

export function showErrorDialog(htmlEncodedMessage: string, requestId?: string, jobId?: string) {
    if (requestId && jobId) {
        htmlEncodedMessage += `<div class="text-muted mt-3 text-right font-weight-light"><small>Job ID: ${jobId}, Request Id: ${requestId}</small></div>`;
    }
    else if (requestId) {
        htmlEncodedMessage += `<div class="text-muted mt-3 text-right font-weight-light"><small>Request Id: ${requestId}</small></div>`;
    }

    bootbox.dialog({
        title: 'Unexpected error occurred',
        message: htmlEncodedMessage,
        size: "large",
        buttons: {
            close: {
                label: 'Close',
            },
        },
    });
}

interface JobAggregateRootEntity {
    id: number;
    aggregateRootEntityName: string;
    aggregateRootEntityId: number;
}

export interface JobDetail {
    id: number;
    guid: string;
    completedOn?: Moment;
    createdOn: Moment;
    type: string;
    inputData: string; // JSON serialized data
    exception?: string;
    numberOfHandlingAttempts: number;
    affectedAggregateRootEntities: JobAggregateRootEntity[];
}

export function waitForJobCompletion(
    jobCompletionUrl: string,
    jobGuid: string,
    submitButton: JQuery<HTMLElement> | undefined,
    onCompletion: (job: JobDetail) => void,
    attemptCount: number
) {
    attemptCount++;

    const url = jobCompletionUrl.replace("$guid", jobGuid);
    $.ajax({
        url: url,
        converters: jQueryAjaxConverters,
        success(data: JobDetail, textStatus: string, jqXhr: JQuery.jqXHR) {
            if (data.completedOn) {
                enableElementAndRemoveSpinner(submitButton);
                onCompletion(data);
            }
            else if (!data.exception || data.numberOfHandlingAttempts < maxJobHandlingAttempts) {
                scheduleWaitForJobCompletionInternal();
            }
            else {
                const requestId = jqXhr.getResponseHeader(REQUEST_ID_HEADER);
                const message = `<div>${htmlEncode(data.exception)}</div>`;

                showErrorDialog(message, `${requestId}`, data.guid);
                console.error("Job completion error", data);
                Sentry.captureMessage('Job completion error: ' + data.exception,
                    {
                        level: Sentry.Severity.Fatal,
                        tags: {
                            triggerAlert: true,
                        },
                        contexts: {
                            job: {
                                requestId,
                                jobId: data.guid,
                            }
                        }
                    }
                )

                enableElementAndRemoveSpinner(submitButton);
            }
        },
        error(xhr, textStatus, errorThrown) {
            if (attemptCount === maxWaitForJobCompletionAttempts) {
                ajaxErrorHandler(xhr, textStatus, errorThrown, url, {}, 'Job completion error');

                enableElementAndRemoveSpinner(submitButton);
            } else {
                scheduleWaitForJobCompletionInternal();
            }
        }
    });

    function scheduleWaitForJobCompletionInternal() {
        scheduleWaitForJobCompletion(jobCompletionUrl, jobGuid, onCompletion, subsequentCompletionTimeout, submitButton, attemptCount);
    }
}

export function disableElementAndAddSpinner(
    element: JQuery<HTMLElement> | undefined,
    shouldClearElementContentBeforeAddingSpinner = false
) {
    if (!element) return;
    element.prop("disabled", true);
    if (shouldClearElementContentBeforeAddingSpinner) {
        element.html("");
    }
    if (element.children("span[class^='spinner-border']").length === 0) {
        element.prepend("<span class='spinner-border spinner-border-sm'></span>");
    }
}

export function enableElementAndRemoveSpinner(element: JQuery<HTMLElement> | undefined) {
    if (!element) return;
    element.prop("disabled", false);
    element.children("span[class^='spinner-border']").remove();
}

function isElementDisabled(element: JQuery<HTMLElement>) : boolean {
    if (!element) return false;
    return element.prop("disabled");
}

export function formSubmitWithSpinner(
    form: HTMLFormElement,
    submitButton: HTMLElement,
    submitEventAnalytics?: EventData,
) {
    if (!form.length) return;

    attachValidationAfterTurboLoad();

    form.onsubmit = () => {
        if (!isFormValid($(form))) return false;

        disableElementAndAddSpinner($(submitButton));

        if (submitEventAnalytics) {
            trackEvent(submitEventAnalytics.eventName, submitEventAnalytics.eventParams);
        }
        
        return true;
    };
}

export function executeCommandWithJobCompletion(
    commandUrl: string,
    getJobUrl: string,
    data: any = undefined,
    isExternalUser = false,
    message: string | undefined = undefined,
    onCompletion: ((jobGuid: string) => void) | undefined = undefined
) {
    $.ajax({
        url: commandUrl,
        method: "POST",
        data: data,
        success(jobGuid) {
            if (!isExternalUser) {
                bootbox.confirm(`${message ? `${message}<br><br>` : ""}${stringFormat(translate(TranslationsKeys.Job_started_message), jobGuid)}`,
                    openJob => {
                        if (openJob) {
                            window.open(getJobUrl.replace('$guid', jobGuid), "_blank");
                        }
                        if (onCompletion) onCompletion(jobGuid);
                    });
            } else {
                if (message) {
                    bootbox.alert(message, () => {
                        if (onCompletion) onCompletion(jobGuid);
                    });
                }
            }
        },
    }).fail((jqXhr, textStatus, errorThrown) => {
        ajaxErrorHandler(jqXhr, textStatus, errorThrown, commandUrl, data);
    });
}